import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminImportarCSVPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [curso, setCurso] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCurso();
  }, [id]);

  const fetchCurso = async () => {
    try {
      const res = await axios.get(`${API_URL}/capacitaciones/admin/cursos/${id}`);
      setCurso(res.data);
    } catch (err) {
      setError('Curso no encontrado');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv')) {
      setError('El archivo debe ser un CSV');
      return;
    }
    
    setFile(selectedFile);
    setError(null);
    setResult(null);
    
    // Preview file content
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const rows = lines.slice(1).map((line, index) => {
        const values = line.split(',').map(v => v.trim());
        const row = {};
        headers.forEach((header, i) => {
          row[header] = values[i] || '';
        });
        row._index = index + 1;
        row._valid = row.nombre && row.apellido && row.dni && row.matricula && row.email;
        return row;
      });
      
      setPreview({ headers, rows });
    };
    reader.readAsText(selectedFile);
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `${API_URL}/capacitaciones/admin/cursos/${id}/importar-csv`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setResult(res.data);
      setFile(null);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  const requiredColumns = ['nombre', 'apellido', 'dni', 'matricula', 'email'];
  const optionalColumns = ['distrito'];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/capacitaciones/cursos/${id}/inscriptos`)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Importar Inscriptos</h1>
          <p className="text-gray-600">{curso?.nombre}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Subir archivo CSV</CardTitle>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Importación completada</h3>
                  <p className="text-gray-600 mb-4">
                    Se importaron {result.importados} de {result.total} registros
                  </p>
                  {result.errores > 0 && (
                    <p className="text-amber-600 text-sm mb-4">
                      {result.errores} registros con errores (duplicados o datos faltantes)
                    </p>
                  )}
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" onClick={() => setResult(null)}>
                      Importar otro archivo
                    </Button>
                    <Button onClick={() => navigate(`/admin/capacitaciones/cursos/${id}/inscriptos`)}>
                      Ver inscriptos
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      file ? 'border-emerald-300 bg-emerald-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      {file ? (
                        <>
                          <FileText className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                          <p className="font-medium text-emerald-700">{file.name}</p>
                          <p className="text-sm text-gray-500 mt-1">Clic para cambiar archivo</p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="font-medium text-gray-700">Arrastrá o hacé clic para subir</p>
                          <p className="text-sm text-gray-500 mt-1">Archivos CSV únicamente</p>
                        </>
                      )}
                    </label>
                  </div>

                  {error && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Preview */}
          {preview && (
            <Card>
              <CardHeader>
                <CardTitle>Vista previa ({preview.rows.length} registros)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">#</th>
                        <th className="p-2 text-left">Estado</th>
                        {requiredColumns.map(col => (
                          <th key={col} className="p-2 text-left capitalize">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {preview.rows.slice(0, 50).map((row, i) => (
                        <tr key={i} className={row._valid ? '' : 'bg-red-50'}>
                          <td className="p-2 text-gray-500">{row._index}</td>
                          <td className="p-2">
                            {row._valid ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                          </td>
                          {requiredColumns.map(col => (
                            <td key={col} className="p-2">{row[col] || '-'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.rows.length > 50 && (
                  <p className="text-center text-sm text-gray-500 py-2">
                    Mostrando 50 de {preview.rows.length} registros
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Import Button */}
          {preview && !result && (
            <Button
              onClick={handleImport}
              disabled={importing}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {importing ? 'Importando...' : `Importar ${preview.rows.filter(r => r._valid).length} registros válidos`}
            </Button>
          )}
        </div>

        {/* Sidebar */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Formato del archivo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium text-sm mb-2">Columnas obligatorias:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {requiredColumns.map(col => (
                    <li key={col} className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      {col}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-sm mb-2">Columnas opcionales:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {optionalColumns.map(col => (
                    <li key={col} className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                      {col}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-medium text-sm mb-2">Ejemplo:</h4>
                <code className="text-xs text-gray-600 block whitespace-pre">
{`nombre,apellido,dni,matricula,email,distrito
Juan,Pérez,30123456,1234,juan@mail.com,Lomas
María,García,30654321,5678,maria@mail.com,Lanús`}
                </code>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                <strong>Nota:</strong> Los DNI duplicados (para el mismo curso) serán ignorados.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
